import { Button, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import {
  IconBell,
  IconBellOff,
  IconCopy,
  IconCopyPlus,
  IconExternalLink,
  IconLink,
  IconLock,
  IconLockOpen,
  IconMenu2,
  IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import { useHistory } from "react-router";
import {
  openapi,
  usePostProjectByProjectIdIssuesCopyMutation,
  usePutProjectByProjectIdIssueArchiveMutation,
  usePutProjectByProjectIdIssueSubscribeMutation,
} from "../../../../../../../openapi";
import { ConfirmAbortModal } from "../../../../../../components/ConfirmAbortModal/ConfirmAbortModal";
import { removeAppModalInRedux, selectUserMember } from "../../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import { useToggleClientAccessMutation } from "../../../issueRTK";
import { issueBoardHasClientAccess } from "../../../issueUtils";
import IssueChangeRegisterSelectorSubMenu from "./IssueChangeItemRegisterSelector";

type IssueItemModalMenuProps = {
  issue: IIssueApi;
  projectId: string;
  issueBoard: IIssueBoardApi;
};

const IssueItemModalMenu = ({ issue, projectId, issueBoard }: IssueItemModalMenuProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const user = useAppSelector((state) => state.globalReducer.user);
  const hasSubscription = issue.subscribers?.indexOf(user?._id) > -1;

  const [archiveIssue] = usePutProjectByProjectIdIssueArchiveMutation();
  const [toggleSubscription] = usePutProjectByProjectIdIssueSubscribeMutation();
  const [postIssueCopy] = usePostProjectByProjectIdIssuesCopyMutation();
  const [toggleClientAccess] = useToggleClientAccessMutation();

  const userMember = useAppSelector(selectUserMember);
  const boardHasClientAccess = issueBoardHasClientAccess(issueBoard);
  const canToggleClientAccess = userMember?.group !== "CLIENT" && boardHasClientAccess;
  const isLockedForCustomers = issue.clientAccess !== true;

  const copyIssue = async (includeTasks: boolean) => {
    await postIssueCopy({
      projectId,
      body: {
        issueId: issue._id,
        includeTasks,
      },
    });

    notifications.show({
      title: "Kopi opprettet",
      message: "En kopi er nå opprettet.",
      color: "green",
      icon: <IconCopy size={16} />,
    });
  };

  const toggleSubscribe = async () => {
    await toggleSubscription({ projectId, body: { issueId: issue._id } });

    notifications.show({
      icon: hasSubscription ? <IconBellOff size={16} /> : <IconBell size={16} />,
      title: hasSubscription ? "Abonnement fjernet" : "Abonnement opprettet",
      message: hasSubscription
        ? "Du vil ikke lenger motta varsler for denne saken."
        : "Du vil nå motta varsler for denne saken.",
      color: "green",
    });
  };

  const toggleCustomerAccess = async () => {
    await toggleClientAccess({ projectId, issueId: issue._id });
    dispatch(openapi.util.invalidateTags(["Issue", "Issues"]));

    notifications.show({
      icon: isLockedForCustomers ? <IconLockOpen size={16} /> : <IconLock size={16} />,
      title: isLockedForCustomers ? "Kundetilgang åpnet" : "Låst for kunder",
      message: isLockedForCustomers
        ? "Kunder har nå tilgang til denne saken."
        : "Kunder har ikke lenger tilgang til denne saken.",
      color: "green",
    });
  };

  const archive = async () => {
    await archiveIssue({
      body: {
        issueId: issue._id,
      },
      projectId,
    });

    dispatch(removeAppModalInRedux(issue._id));
    history.replace(`${history.location.pathname}`);

    notifications.show({
      title: "Slettet",
      message: "Saken er nå slettet.",
      color: "green",
      icon: <IconTrash size={16} />,
    });
  };

  const currentUrl = window.location.href;
  const issueUrl = `${window.location.origin}/#/project/${projectId}/issues/${issue.issueBoard._id}?id=${issue._id}`;

  return (
    <>
      <ConfirmAbortModal
        isOpen={openConfirm}
        onCancel={() => setOpenConfirm(false)}
        onConfirm={async () => {
          await archive();
          setOpenConfirm(false);
        }}
        customHeader="Bekreft sletting"
        customText="Er du sikker på at du vil slette denne saken?"
        customButtonText="Bekreft"
      />
      <Menu position="bottom-start">
        <Menu.Target>
          <Button variant="light">
            <IconMenu2 size={16} />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {hasSubscription === true ? (
            <Menu.Item leftSection={<IconBellOff size={16} />} onClick={() => toggleSubscribe()}>
              Fjern abonnement
            </Menu.Item>
          ) : (
            <Menu.Item leftSection={<IconBell size={16} />} onClick={() => toggleSubscribe()}>
              Abonner
            </Menu.Item>
          )}
          <Menu.Item
            onClick={async () => {
              await navigator.clipboard.writeText(issueUrl);

              notifications.show({
                title: "Lenke kopiert",
                icon: <IconLink size={16} />,
                message: "Lenken til denne saken er nå kopiert til utklippstavlen.",
                color: "green",
              });
            }}
            leftSection={<IconLink size={16} />}
          >
            Kopier lenke
          </Menu.Item>
          <Menu.Item
            disabled={currentUrl === issueUrl}
            onClick={() => {
              history.push(`/project/${projectId}/issues/${issue.issueBoard._id}?id=${issue._id}`);
            }}
            leftSection={<IconExternalLink size={16} />}
          >
            Gå til register
          </Menu.Item>
          <IssueChangeRegisterSelectorSubMenu issue={issue} projectId={projectId} activeBoard={issue.issueBoard} />
          {canToggleClientAccess && (
            <Menu.Item
              onClick={() => toggleCustomerAccess()}
              leftSection={isLockedForCustomers ? <IconLockOpen size={16} /> : <IconLock size={16} />}
            >
              {isLockedForCustomers ? "Åpne for kunder" : "Lås for kunder"}
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item onClick={() => copyIssue(false)} leftSection={<IconCopy size={16} />}>
            Lag kopi
          </Menu.Item>
          <Menu.Item onClick={() => copyIssue(true)} leftSection={<IconCopyPlus size={16} />}>
            Lag kopi med aksjoner
          </Menu.Item>
          {issueBoard?.disableDeleteIssue !== true && (
            <>
              <Menu.Divider />
              <Menu.Item
                onClick={() => {
                  setOpenConfirm(true);
                }}
                color="red"
                leftSection={<IconTrash size={16} />}
              >
                Slett
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export default IssueItemModalMenu;
